import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import Alert from 'antd/es/alert';

import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { useMobileView } from '@common/react/hooks/useMobileView';
import { ItemsProvider, SortingDirection } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ExternalHtml from '@common/react/components/UI/ExternalHtml/ExternalHtml';

import { Specialty } from '@app/objects/Specialty';
import DoctorCard from '@app/components/UI/DoctorCard/DoctorCardHome';

import InquiryForm from '@app/components/Forms/InquiryForm';
import LoginForm from '@app/components/UI/ScheduleAppointmentModal/LoginForm';
import ClinicCard from '@app/components/Pages/Clinics/ClinicCard';
import { Special } from '@app/objects/Special';
import SpecialsCarousel from '@app/components/UI/SpecialsCarousel/SpecialsCarousel';
import { User } from '@app/objects/User';

import '@app/scss/pages/clinicsPage.scss';
import '@app/scss/pages/home.scss';
import '@app/scss/pages/doctor.scss';
import '@app/scss/pages/region.scss';

const navMenu = [
	{
		title: 'About',
		check: (item) => !!item?.text,
		id: 'about',
	},
	{
		title: 'Book Online',
		check: (item, isMobile) => isMobile,
		id: 'bookOnline',
		className: 'show-mobile show-mobile__inline-block',
	},
	{
		title: 'Clinics',
		check: (item) => item?.locations.length > 0,
		id: 'locations',
	},
	{
		title: 'Providers',
		check: (item) => item?.doctors.length > 0,
		id: 'doctors',
	},
	{
		title: 'Specials',
		check: (item) => item?.specials.length > 0,
		id: 'specials',
		className: '',
	},
];

const SpecialtyInner: React.FC<{specialty: Specialty}> = ({ specialty }) => {
	const { getUser } = useApplicationContext();
	const user = getUser<User>();
	const location = useLocation();

	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [loginVisible, setLoginVisible] = React.useState(false);
	const toggle = () => setIsOpen(!isOpen);
	const locationsForInquiry = specialty.locations as any || [];
	const doctorsForInquiry = specialty.doctors.map((item) => item.doctor) as any || [];
	const isMobile = useMobileView(768);
	const specials = specialty.specials.map((item) => ({
		...item,
		location: item.location || specialty.locations.find((location) => location.id === item.locationId),
	}));
	const inquiry = (<div className="inquiry-wrapper">
		<div className="contact-text">
			<Alert
				message={<>
					Leave inquiry and clinic front desk will contact you soon for help.
					{!user ? <>
						<br />
						Already have account? -
						{' '}
						<a onClick={() => setLoginVisible(true)}>Login</a>
						{' '}
						for fast fill form.
					</> : null}
				</>
				}
				type="info"
			/>
		</div>
		<InquiryForm
			locations={locationsForInquiry}
			doctors={doctorsForInquiry}
			doctorId={doctorsForInquiry[0]?.id}
		/>
	</div>);

	return <>
		<LoginForm visible={loginVisible} handleClose={() => setLoginVisible(false)} afterLogin={() => setLoginVisible(false)} />
		<div className="site-headline doctor-portal region-page">
			<div className="doctor-portal-container">
				<div className="px-50 mb20 doctor-portal-content">
					<div className="max-content main-content">
						<BreadCrumbs
							links={[
								{
									title: 'Specialties',
									path: '/specialties',
								},
							]}
							title={specialty?.name}
						/>
						<div className="mb20 px15 main-content-block" style={{ marginBottom: 20 }}>
							<div
								className="doctor-menu-content"
								style={{
									flexDirection: 'row', flexWrap: 'wrap', gap: 15, alignItems: 'center',
								}}
							>
								<h1 style={{ margin: 0 }}>{specialty?.name}</h1>
								<nav className="doctor-portal__nav pull-right" style={{ margin: '0 0 0 auto' }}>
									<ul>
										{navMenu.filter((value) => value.check(specialty, isMobile)).map((item) =>
											<li key={item.id} className={item.className}>
												<a href={`${location?.pathname}#${item.id}`}>{item.title}</a>
											</li>)
										}
									</ul>
								</nav>
							</div>
						</div>
						<div className="doctor-other-content">
							{specialty?.text && <div className="doctor-portal__about scroll-top" id="about">
								<h3>
									About
									{' '}
									{specialty?.name}
								</h3>

								{specialty?.text
									? <div className="inner-page__headline column-md-4 mb10">
										<div className="column-md-4">
											<ExternalHtml html={specialty?.text} />
										</div>
									</div> : <div className="mb10">-</div>
								}
							</div>}
							<div className="show-touch show-touch__block">
								<h3 id="bookOnline" className="scroll-top">Book Online</h3>
								<div className="schedule-appointment__form">
									{inquiry}
								</div>
							</div>
							{specialty.locations?.length > 0 ? <h3 id="locations" className="scroll-top">Clinics</h3> : null}
							{locationsForInquiry?.length ? <div className="clinics-page">
								{locationsForInquiry.map((item) => <ClinicCard key={item.id} location={item} />)}
							</div> : null}
							{doctorsForInquiry?.length > 0 && <div>
								<br />
								<h2 className="scroll-top" id="doctors">Providers</h2>
								<div className="home__container doctor-card-list" style={{ paddingLeft: 0, paddingRight: 0 }}>
									<div className="mb10 doctors-list">
										{doctorsForInquiry.map((doctor) => <DoctorCard
											doctor={doctor as any}
											key={doctor.id}
										/>)}
									</div>
								</div>
							</div>}
							<ItemsProvider<Special>
								type="special"
								loadRequest="specialsList"
								items={specials}
								filters={{
									specialtyId: specialty.id,
									column: [
										{ caption: 'name', direction: SortingDirection.Ascending },
										{ caption: 'price', direction: SortingDirection.Default }],
								}}
								skipInitLoad={!!specials}
							>
								{({ state: { items } }) => <>
									{items.length > 0 ? <div className="is-relative">
										<div className="scroll-top" id="specials" />
										<NavLink
											className="btn btn-primary btn-more pull-right"
											to={{ pathname: '/specials', search: `specialties=[${specialty.id}]` }}
										>
											View All
										</NavLink>
										<SpecialsCarousel specials={items} count={3} titleAsH2 />
									</div> : null}
								</>}
							</ItemsProvider>
						</div>
					</div>
					<div className="schedule-appointment__wrapper mobile-hide">
						<div className={`schedule-appointment__form ${
							isOpen ? 'schedule-appointment__open' : 'schedule-appointment__close'} schedule-appointment__inquiry-tab`}
						>
							<button
								type="button"
								className="btn btn-sm btn-default schedule-appointment__button pull-right"
								onClick={toggle}
							>
								<span />
							</button>
							{inquiry}
						</div>
					</div>
				</div>
			</div>
		</div>
	</>;
};

const SpecialtyPage: React.FC = () => {
	return (
		<ServerPageProvider
			reloadByPathChange
			path="specialties"
			defaultTitle="Specialty"
			inner={(page) => (
				<SpecialtyInner
					specialty={page?.item}
				/>
			)}
		/>
	);
};

export default SpecialtyPage;
